<template>
    <div>
        <Dialog v-model:visible="visible" :style="{ width: '450px' }" header="Configure links for your step" :modal="true" @hide="emit('hide-dialog', true)" :dismissableMask="true">
            <!-- Link type selection starts
                This part basically serves only for processing in front
                to adjust the form type to show to avoid possible user errors
            -->
            <div class="p-fluid formgrid grid">
                <div class="field col-12">
                    <label for="type" v-tooltip="'Select the type of link you want to create'">Link Type:</label>

                    <Dropdown id="type" v-model="linkStore.link.type" :options="linkStore.types" optionLabel="label" optionValue="name" placeholder="Select the link type" />
                </div>
                <Divider />
                <div v-if="linkStore.link.type === 'boolean' || linkStore.link.type === 'value'" class="field col-12">
                    <label for="formula">Formula:</label>
                    <Dropdown id="formula" :class="{ 'p-invalid': submitted && !linkStore.link.formula_id }" v-model="linkStore.link.formula_id" :options="filteredFormulas" optionLabel="code" optionValue="id" placeholder="Select a formula" />
                    <small class="p-invalid" v-if="submitted && !linkStore.link.formula_id">Formula is a required field.</small>
                </div>
                <div v-if="linkStore.link.type === 'attribute'" class="field col-12">
                    <label for="attribute">Attribute:</label>
                    <!-- put here attributes later instead of filteredFormulas-->
                    <Dropdown
                        id="attribute"
                        :class="{ 'p-invalid': submitted && !linkStore.link.attribute_code }"
                        v-model="linkStore.link.attribute_code"
                        :options="filteredAttributes"
                        optionLabel="label"
                        optionValue="code"
                        placeholder="Select an attribute"
                        @change="checkAttributeType"
                        :showClear="true"
                        :filter="true"
                    />
                    <small class="p-invalid" v-if="submitted && !linkStore.link.attribute_code">Attribute is a required field.</small>
                </div>
                <div v-if="linkStore.link.type === 'attribute' || linkStore.link.type === 'value'" class="field col-6">
                    <label for="operator">Operator:</label>
                    <Dropdown id="operator" :class="{ 'p-invalid': submitted && !linkStore.link.operator }" v-model="linkStore.link.operator" :options="linkStore.operators" optionLabel="label" optionValue="value" placeholder="Select an operator" />
                    <small class="p-invalid" v-if="submitted && !linkStore.link.operator">Operator is a required field.</small>
                </div>
                <div v-if="linkStore.link.type === 'attribute' || linkStore.link.type === 'value'" class="field col-6">
                    <label for="value">Value:</label>
                    <Dropdown
                        v-if="optionsType"
                        id="value"
                        :class="{ 'p-invalid': submitted && !linkStore.link.value }"
                        v-model="linkStore.link.value"
                        :options="attributeOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select an option"
                        :showClear="true"
                    />
                    <InputNumber v-else v-model="linkStore.link.value" :class="{ 'p-invalid': submitted && !linkStore.link.value }" placeholder="Enter numeric value" />
                    <small class="p-invalid" v-if="submitted && !linkStore.link.value">Value is a required field.</small>
                </div>
                <div class="field col-12">
                    <label for="step-to">Destination Step:</label>
                    <Dropdown id="step-to" :class="{ 'p-invalid': submitted && !linkStore.link.step_to }" v-model="linkStore.link.step_to" :options="destinationSteps" optionLabel="label" optionValue="id" placeholder="Select the destination step" />
                    <small class="p-invalid" v-if="submitted && !linkStore.link.step_to">Destination step is a required field.</small>
                </div>
            </div>
            <template #footer>
                <Button label="Submit" @click="submit" />
            </template>
        </Dialog>
    </div>
</template>

<script setup>
import { ref, watch, computed, inject, defineEmits, defineProps, onMounted } from 'vue';
const stepStore = inject('stepStore');
const linkStore = inject('linkStore');
const formulas = inject('formulas');
const props = defineProps({
    mode: {
        type: String,
        default: 'add',
    },
});
const optionsType = ref(false);
const attributeOptions = ref([]);
const emit = defineEmits(['hide-dialog']);
const visible = ref(true);
const submitted = ref(false);

onMounted(() => {
    if (linkStore.link.attribute_code) {
        checkAttributeType();
    }
});

// generates the active currentLinkType so that everytime it changes
const currentLinkType = computed(() => {
    return linkStore?.link?.type;
});

// list of all filtered attributes
const filteredAttributes = computed(() => {
    const attributes = stepStore.activeStep?.block?.form_attributes ?? stepStore.activeStep?.block?.attributes;
    return attributes?.filter((attribute) => [5, 8, 9, 10, 11].includes(parseInt(attribute.type)));
});

// watch for the link type changes to refresh the submitted value
// just in case link has been submitted with validation errors and user changes link type to avoid the errors shown
watch(currentLinkType, () => {
    // nullify the link keys where type doesnt matches
    switch (currentLinkType.value) {
        case 'default':
            linkStore.link.formula_id = null;
            linkStore.link.attribute_code = null;
            linkStore.link.operator = null;
            linkStore.link.value = null;
            break;

        case 'boolean':
            linkStore.link.attribute_code = null;
            linkStore.link.operator = null;
            break;

        case 'value':
            linkStore.link.attribute_code = null;
            break;

        case 'attribute':
            linkStore.link.formula_id = null;
            break;
    }
    submitted.value = false;
});

// filters out formula on the basis of link type selected
// basically filters out boolean or value based formula
const filteredFormulas = computed(() => {
    const formula_type = linkStore.link.type === 'boolean' ? '1' : linkStore.link.type === 'value' ? '2' : null;
    return formulas.value?.filter((formula) => formula.type === formula_type);
});

// list out the filtered destination steps
// first case:- cannot be itself and second case:- cannot be first step (to avoid loop)
const destinationSteps = computed(() => {
    const activeStepId = stepStore?.activeStep?.id;
    return stepStore.steps?.filter((step) => step.entry !== 1 && step.id !== activeStepId);
});

const checkAttributeType = () => {
    const attributeCode = linkStore.link.attribute_code;
    const attributeArr = stepStore.steps
        ?.filter((step) => step.block) //filter out ones null blocks
        ?.map((step) => step.block) // map out block from existing steps
        ?.map((block) => block.attributes)
        .flat(Infinity);
    const attributeDetails = attributeArr.find((attribute) => attribute.code === attributeCode);
    if ([8, 9, 10, 11].includes(parseInt(attributeDetails?.type))) {
        attributeOptions.value = attributeDetails.options;
        optionsType.value = true;
    } else {
        attributeOptions.value = [];
        optionsType.value = false;
    }
};

// const attribute_type = block.attributes.forEach((attribute) => {
//                 if (attribute.code === attributeCode) {
//                     return attribute.type;
//                 }
//             });
//             return attribute_type;

const submit = async () => {
    submitted.value = true;
    if (isValid()) {
        // check if its in edit mode
        if (props.mode === 'edit') {
            await updateLink();
        } else {
            await addLink();
        }
    }
};

const addLink = async () => {
    // remove the type
    delete linkStore.link.type;
    // append the remaining information
    linkStore.link.step_from = stepStore.activeStep.id;
    const link = await linkStore.createLink(linkStore.link);
    if (link?.id) {
        emit('hide-dialog', true);
    }
};

const updateLink = async () => {
    const link = await linkStore.updateLink(linkStore.link);
    if (link?.id) {
        // remove the type
        delete linkStore.link.type;
        emit('hide-dialog', true);
    }
};

const isValid = () => {
    // step to is by default required in all
    if (!linkStore.link.step_to) {
        return false;
    }
    switch (linkStore.link.type) {
        case 'boolean':
            return !linkStore.link.formula_id ? false : true;
        case 'value':
            return !linkStore.link.formula_id && !linkStore.link.operator && !linkStore.link.value ? false : true;
        case 'attribute':
            return !linkStore.link.attribute_code && !linkStore.link.operator && !linkStore.link.value ? false : true;
    }

    return true;
};
</script>
